import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { DueTodayRow } from "app/components/no-touch-purchase-modal/summary-modal/details/order-summary/StyledOrderSummary";
import type { IPurchaseDetails } from "app/components/no-touch-purchase-modal/summary-modal/details/useNoTouchPurchaseSummaryModalDetails";
import type { INoTouchAddon } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { getSingleDiscountValue } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { discount, NO_TOUCH_PURCHASE_TRANSLATIONS } from "app/components/no-touch-purchase-modal/translations";
import type { AddonTypeName } from "app/contexts/AddonStateContext/const";
import { getCurrency } from "app/pages/digital-suite/home-page/components/AfterTrialPopup/utils";
import { Divider } from "app/pages/market-research/components/Divider";

const LoadingRow = ({ height = 20 }: { height?: number }) => (
    <>
        <PixelPlaceholderLoader
            width={185}
            height={height}
        />
        <PixelPlaceholderLoader
            width={50}
            height={height}
        />
    </>
);

interface IOrderSummaryProps {
    selectIsLoading: boolean;
    isMonthly: boolean;
    isLoading: boolean;
    addonType: AddonTypeName;
    currency: string;
    vatPercent?: number;
    selectedAddon: INoTouchAddon;
    purchaseDetails: IPurchaseDetails;
    isOneClickSmallTest: boolean;
}

export const OrderSummary = ({
    selectIsLoading,
    isMonthly,
    isLoading,
    addonType,
    currency,
    vatPercent,
    selectedAddon,
    purchaseDetails,
    isOneClickSmallTest,
}: IOrderSummaryProps) => {
    const { translate } = useTranslate();

    const discountValue = getSingleDiscountValue(selectedAddon);

    const periodText = translate(
        isMonthly
            ? NO_TOUCH_PURCHASE_TRANSLATIONS.details.details.periodTextMonthly
            : NO_TOUCH_PURCHASE_TRANSLATIONS.details.details.periodTextAnnually,
    );

    return (
        <Flex
            flexDirection="column"
            gap="12px"
            width={"100%"}
        >
            {selectIsLoading ? (
                <PixelPlaceholderLoader
                    width={75}
                    height={20}
                />
            ) : (
                <Text fontVariant="caption">{translate(NO_TOUCH_PURCHASE_TRANSLATIONS.details.details.caption)}</Text>
            )}

            <Flex
                justifyContent="space-between"
                gap="8px"
            >
                {isLoading || selectIsLoading ? (
                    <LoadingRow />
                ) : (
                    <>
                        <Text
                            fontVariant="body2"
                            flex={1}
                        >
                            {selectedAddon?.displayName ||
                                translate(
                                    NO_TOUCH_PURCHASE_TRANSLATIONS.summary[addonType].selectText[
                                        selectedAddon?.quantity ?? 0
                                    ],
                                )}
                        </Text>
                        <Text fontVariant="body2">
                            <strong>
                                {getCurrency(currency).sign}
                                {(purchaseDetails?.price ?? 0).toLocaleString()} / {periodText}
                            </strong>
                        </Text>
                    </>
                )}
            </Flex>

            <Flex
                justifyContent="space-between"
                gap="8px"
            >
                {isLoading || selectIsLoading ? (
                    <LoadingRow />
                ) : (
                    <>
                        <Text
                            fontVariant="body2"
                            flex={1}
                        >
                            {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.details.details.vat)}
                            {vatPercent ? ` (${vatPercent}%)` : null}
                        </Text>
                        <Text fontVariant="body2">
                            <strong>
                                {getCurrency(currency).sign}
                                {(purchaseDetails?.tax ?? 0).toLocaleString()} / {periodText}
                            </strong>
                        </Text>
                    </>
                )}
            </Flex>
            {discountValue ? (
                <Flex
                    justifyContent="space-between"
                    gap="8px"
                >
                    {isLoading || selectIsLoading ? (
                        <LoadingRow />
                    ) : (
                        <>
                            <Text
                                fontVariant="body2"
                                flex={1}
                            >
                                {translate(discount.addonsDiscountOff, {
                                    percent: `${discountValue * 100}`,
                                })}
                            </Text>
                            <Text fontVariant="body2">
                                <strong>
                                    -{getCurrency(currency).sign}
                                    {((purchaseDetails?.price ?? 0) * discountValue).toLocaleString()} / {periodText}
                                </strong>
                            </Text>
                        </>
                    )}
                </Flex>
            ) : null}

            <Divider />

            <DueTodayRow
                justifyContent="space-between"
                alignItems="center"
                gap="8px"
                isOneClickSmallTest={isOneClickSmallTest}
            >
                {isLoading || selectIsLoading ? (
                    <LoadingRow height={24} />
                ) : (
                    <>
                        <Flex alignItems="center">
                            <Text
                                fontVariant="subtitle2"
                                flex={1}
                            >
                                {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.details.details.dueToday)}
                            </Text>
                            <Tooltip
                                content={
                                    purchaseDetails
                                        ? translate(NO_TOUCH_PURCHASE_TRANSLATIONS.details.details.infoTooltip, {
                                              regularPrice: `${getCurrency(currency).sign}${(
                                                  (purchaseDetails?.price ?? 0) + (purchaseDetails?.tax ?? 0)
                                              ).toLocaleString()}`,
                                              period: translate(isMonthly ? "monthly" : "annually"),
                                          })
                                        : null
                                }
                            >
                                <Info24px
                                    size="sm"
                                    color={isOneClickSmallTest ? colorsPalettes.carbon[200] : "inherit"}
                                />
                            </Tooltip>
                        </Flex>
                        <Text fontVariant="subtitle2">
                            <strong>
                                {getCurrency(currency).sign}
                                {(purchaseDetails?.dueToday ?? 0).toLocaleString()} / {periodText}
                            </strong>
                        </Text>
                    </>
                )}
            </DueTodayRow>

            <Flex justifyContent="space-between">
                {selectIsLoading ? (
                    <LoadingRow />
                ) : (
                    <>
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.details.details.nextBilling)}
                        </Text>
                        {isLoading ? (
                            <PixelPlaceholderLoader
                                width={50}
                                height={20}
                            />
                        ) : (
                            <Text
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                {purchaseDetails?.nextBillingPeriod.toLocaleDateString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                }) ?? "-"}
                            </Text>
                        )}
                    </>
                )}
            </Flex>
        </Flex>
    );
};
